import React, { useState, useEffect } from 'react';
import { db } from '../firebase'; // Ensure this path is correct
import { collection, getDocs, query, where, documentId, Timestamp, addDoc, writeBatch, doc, updateDoc } from 'firebase/firestore';
import './KitchenDashboard.css';

// Helper functions (no changes)
const getDayNameFromDate = (dateString) => {
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const date = new Date(dateString);
  const dayName = days[date.getDay()]; // Use getDay() instead of getUTCDay()
  console.log(`Date: ${dateString}, Day: ${dayName}`);
  return dayName;
};

// Helper function to try different day name formats
const getMenuForDay = (weeklyMenu, dayName, selectedDate) => {
  if (!weeklyMenu) return undefined;
  
  // Check if weeklyMenu is an array of objects with dayName property
  if (Array.isArray(weeklyMenu) && weeklyMenu.length > 0 && weeklyMenu[0].dayName) {
    console.log(`Menu is array of objects with dayName. Looking for day: ${dayName}`);
    console.log(`Available days:`, weeklyMenu.map(day => day.dayName));
    
    // Find the day object that matches the dayName
    const dayObject = weeklyMenu.find(day => day.dayName === dayName);
    if (dayObject) {
      console.log(`Found day object for ${dayName}:`, dayObject);
      return dayObject.meals || {};
    } else {
      console.log(`No day object found for ${dayName}. Available days:`, weeklyMenu.map(day => day.dayName));
      return undefined;
    }
  }
  
  // Check if weeklyMenu is a simple array (index-based: 0=Sunday, 1=Monday, etc.)
  if (Array.isArray(weeklyMenu)) {
    const date = new Date(selectedDate);
    const dayIndex = date.getDay(); // 0=Sunday, 1=Monday, etc.
    console.log(`Menu is simple array-based. Day: ${dayName}, Index: ${dayIndex}, Selected Date: ${selectedDate}`);
    return weeklyMenu[dayIndex];
  }
  
  // Handle object-based menu (string keys)
  // Try exact match first
  if (weeklyMenu[dayName]) {
    return weeklyMenu[dayName];
  }
  
  // Try different formats
  const dayVariations = [
    dayName,
    dayName.toLowerCase(),
    dayName.toUpperCase(),
    dayName.substring(0, 3), // Mon, Tue, etc.
    dayName.substring(0, 3).toLowerCase(),
    dayName.substring(0, 3).toUpperCase()
  ];
  
  for (const variation of dayVariations) {
    if (weeklyMenu[variation]) {
      console.log(`Found menu using variation: ${variation} for day: ${dayName}`);
      return weeklyMenu[variation];
    }
  }
  
  console.log(`No menu found for day: ${dayName}. Available keys:`, Object.keys(weeklyMenu));
  return undefined;
};
const MEAL_ORDER = ['Breakfast', 'Lunch', 'Dinner', 'Snack'];

const KitchenDashboard = () => {
  // --- STATE --- (no changes)
  const [prepByPackage, setPrepByPackage] = useState({});
  const [foodSummary, setFoodSummary] = useState({});
  const [addonSummary, setAddonSummary] = useState({});
  const [packageCounts, setPackageCounts] = useState({});
  const [totalPackages, setTotalPackages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- UI STATE --- (no changes)
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [weekDays, setWeekDays] = useState([]);
  const [activeView, setActiveView] = useState('packages');
  const [activeMealTab, setActiveMealTab] = useState('');

  // --- NEW STATE FOR REQUEST FORM ---
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestFormData, setRequestFormData] = useState({
    mealTime: '',
    specialInstructions: '',
    priority: 'normal',
    items: []
  });
  const [submittingRequest, setSubmittingRequest] = useState(false);

  // --- NEW STATE FOR AVAILABLE FOOD ITEMS ---
  const [availableFoodItems, setAvailableFoodItems] = useState([]);
  const [loadingFoodItems, setLoadingFoodItems] = useState(false);

  // --- NEW STATE FOR PRODUCTS/INGREDIENTS ---
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedIngredients, setSelectedIngredients] = useState([]);

  // --- NEW STATE FOR CHEF ASSIGNMENT ---
  const [chefs, setChefs] = useState([]);
  const [loadingChefs, setLoadingChefs] = useState(false);
  const [chefAssignments, setChefAssignments] = useState({});

  // --- NEW STATE FOR DEBUG MODE ---
  const [debugMode, setDebugMode] = useState(false);

  // Generate 7-day selector (no changes)
  useEffect(() => {
    const days = [];
    const today = new Date();
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      days.push({
        date: date.toISOString().split('T')[0],
        dayName: date.toLocaleString('en-US', { weekday: 'short' }),
        dayNumber: date.getDate(),
      });
    }
    setWeekDays(days);
  }, []);

  // Fetch chefs and existing assignments
  useEffect(() => {
    fetchChefs();
    fetchExistingAssignments();
  }, [selectedDate]);

  useEffect(() => {
    const getKitchenData = async () => {
      setLoading(true);
      setError(null);
      console.log(`Fetching data for ${selectedDate}...`);
      console.log(`Current date: ${new Date().toISOString()}`);
      console.log(`Selected date: ${selectedDate}`);
      console.log(`Selected date object: ${new Date(selectedDate)}`);

      try {
        let tempPackagePrepList = {};
        let tempFoodSummary = {};
        let tempPackageCounts = {};
        let tempTotalPackages = 0;
        let tempAddonSummary = {};

        // --- Part 1: Process Subscription-Based Meals ---
        const subscriptionsRef = collection(db, 'user_subscriptions');
        const activeSubQuery = query(subscriptionsRef, where("status", "==", "active"));
        const activeSubSnapshots = await getDocs(activeSubQuery);

        console.log(`Total subscriptions found: ${activeSubSnapshots.docs.length}`);

        const selectedDateObj = new Date(selectedDate);
        selectedDateObj.setUTCHours(0, 0, 0, 0);

        const allActiveSubscriptions = activeSubSnapshots.docs.filter(doc => {
          const data = doc.data();
          console.log('Subscription data:', data);
          
          // In debug mode, show all active subscriptions regardless of date
          if (debugMode) {
            console.log(`Debug mode: including subscription ${doc.id}`);
            return true;
          }
          
          // Handle different date formats
          let startDate;
          if (data?.subscriptionStartDate?.toDate) {
            startDate = data.subscriptionStartDate.toDate();
          } else if (data?.subscriptionStartDate?.seconds) {
            startDate = new Date(data.subscriptionStartDate.seconds * 1000);
          } else if (data?.subscriptionStartDate) {
            startDate = new Date(data.subscriptionStartDate);
          }
          
          if (!startDate) {
            console.log('No start date found for subscription:', doc.id);
            return false;
          }
          
          startDate.setUTCHours(0, 0, 0, 0);
          const isActive = selectedDateObj >= startDate;
          console.log(`Subscription ${doc.id}: startDate=${startDate}, selectedDate=${selectedDateObj}, isActive=${isActive}`);
          return isActive;
        });

        console.log(`Found ${allActiveSubscriptions.length} active subscriptions for ${selectedDate}.`);

        // Debug: Log all subscription details
        allActiveSubscriptions.forEach((sub, index) => {
          const data = sub.data();
          console.log(`Subscription ${index + 1}:`, {
            id: sub.id,
            packageId: data.packageId,
            status: data.status,
            startDate: data.subscriptionStartDate,
            customerName: data.customerName
          });
        });

        if (allActiveSubscriptions.length > 0) {
          const uniquePackageIds = [...new Set(allActiveSubscriptions.map(doc => doc.data().packageId))];
          console.log('Unique package IDs:', uniquePackageIds);
          
          if (uniquePackageIds.length > 0) {
            const packagesRef = collection(db, 'packages');
            const packagesQuery = query(packagesRef, where(documentId(), 'in', uniquePackageIds));
            const packageSnapshots = await getDocs(packagesQuery);
            console.log(`Found ${packageSnapshots.docs.length} packages out of ${uniquePackageIds.length} requested`);
            
            // If no packages found with specific IDs, fetch all packages for debugging
            if (packageSnapshots.docs.length === 0) {
              console.log('No packages found with specific IDs, fetching all packages for debugging...');
              const allPackagesSnapshot = await getDocs(packagesRef);
              console.log(`Total packages in database: ${allPackagesSnapshot.docs.length}`);
              allPackagesSnapshot.forEach(doc => {
                console.log(`All package ${doc.id}:`, doc.data());
              });
            }
            
            const packagesMap = new Map();
            packageSnapshots.forEach(doc => {
              packagesMap.set(doc.id, doc.data());
              console.log(`Package ${doc.id}:`, doc.data());
            });

            const dayName = getDayNameFromDate(selectedDate);
            console.log(`Day name for ${selectedDate}: ${dayName}`);
            
            const relevantSubscriptions = allActiveSubscriptions.filter(sub => {
              const packageId = sub.data().packageId;
              const pkg = packagesMap.get(packageId);
              const todaysMenu = getMenuForDay(pkg?.weeklyMenu, dayName, selectedDate);
              const hasMenu = todaysMenu !== undefined;
              console.log(`Package ${packageId} has menu for ${dayName}:`, hasMenu);
              if (!pkg) {
                console.log(`Package ${packageId} not found in packagesMap`);
              } else {
                console.log(`Package ${packageId} weeklyMenu structure:`, pkg.weeklyMenu);
                console.log(`Available days in menu:`, Object.keys(pkg.weeklyMenu || {}));
                console.log(`Todays menu result:`, todaysMenu);
              }
              return hasMenu;
            });

            console.log(`Relevant subscriptions with menu: ${relevantSubscriptions.length}`);
            console.log(`Relevant subscription IDs:`, relevantSubscriptions.map(sub => sub.id));

            tempTotalPackages = relevantSubscriptions.length;
            relevantSubscriptions.forEach(sub => {
              const packageId = sub.data().packageId;
              tempPackageCounts[packageId] = (tempPackageCounts[packageId] || 0) + 1;
            });

            console.log('Package counts:', tempPackageCounts);

            Object.entries(tempPackageCounts).forEach(([packageId, count]) => {
              const pkg = packagesMap.get(packageId);
              const todaysMenu = getMenuForDay(pkg.weeklyMenu, dayName, selectedDate) || {};
              console.log(`Processing package ${packageId} (${pkg.name}) for ${dayName}:`, todaysMenu);
              
              tempPackagePrepList[packageId] = { packageName: pkg.name, meals: todaysMenu };

              Object.entries(todaysMenu).forEach(([mealIndex, mealItems]) => {
                // Map meal index to meal name
                const mealNames = ['Breakfast', 'Lunch', 'Dinner', 'Snack'];
                const mealName = mealNames[parseInt(mealIndex)] || `Meal ${mealIndex}`;
                
                console.log(`Processing meal ${mealName} (index ${mealIndex}):`, mealItems);
                if (!tempFoodSummary[mealName]) { tempFoodSummary[mealName] = {}; }
                
                // Handle different meal structures
                let itemsToProcess = [];
                
                if (Array.isArray(mealItems)) {
                  // If mealItems is an array of objects with mealName and items
                  if (mealItems.length > 0 && mealItems[0].mealName) {
                    // Find the meal that matches the mealName
                    const mealObject = mealItems.find(meal => meal.mealName === mealName);
                    if (mealObject && Array.isArray(mealObject.items)) {
                      itemsToProcess = mealObject.items;
                    } else {
                      itemsToProcess = mealItems; // Fallback to direct array
                    }
                  } else {
                    itemsToProcess = mealItems; // Direct array of items
                  }
                } else if (mealItems && Array.isArray(mealItems.items)) {
                  // If mealItems is an object with items array
                  itemsToProcess = mealItems.items;
                } else {
                  itemsToProcess = []; // No items found
                }
                
                console.log(`Items to process for ${mealName}:`, itemsToProcess);
                
                if (Array.isArray(itemsToProcess)) {
                  itemsToProcess.forEach(item => {
                    const foodName = item.foodItemName || 'Unknown Item';
                    const quantity = (item.quantity || 0) * count;
                    console.log(`Food item: ${foodName}, quantity: ${quantity}, unit: ${item.unit}`);
                    
                    if (tempFoodSummary[mealName][foodName]) {
                      tempFoodSummary[mealName][foodName].totalQuantity += quantity;
                    } else {
                      tempFoodSummary[mealName][foodName] = { 
                        totalQuantity: quantity, 
                        unit: item.unit || 'unit(s)' 
                      };
                    }
                  });
                }
              });
            });
          } else {
            console.log('No unique package IDs found');
          }
        } else {
          console.log('No active subscriptions found');
        }

        // --- Part 2: Process Approved Add-On ORDERS ---
        const startOfDay = Timestamp.fromDate(new Date(`${selectedDate}T00:00:00`));
        const endOfDay = Timestamp.fromDate(new Date(`${selectedDate}T23:59:59`));

        const addonsRef = collection(db, 'Addon_Approved');
        const addonsQuery = query(addonsRef, where('approvedAt', '>=', startOfDay), where('approvedAt', '<=', endOfDay));
        const addonsSnapshot = await getDocs(addonsQuery);
        console.log(`Found ${addonsSnapshot.docs.length} approved addon orders for ${selectedDate}.`);

        // Loop through each approved ORDER document
        addonsSnapshot.forEach(doc => {
          const orderData = doc.data();
          console.log('Addon order data:', orderData);
          
          // Check if the order has an 'items' array
          if (Array.isArray(orderData.items)) {
            // Loop through each ITEM within the order's 'items' array
            orderData.items.forEach(item => {
              const foodName = item.foodItemName;
              const quantity = Number(item.quantity) || 0;

              if (foodName && quantity > 0) {
                // Add the quantity of this item to the summary
                tempAddonSummary[foodName] = (tempAddonSummary[foodName] || 0) + quantity;
                console.log(`Addon item: ${foodName}, quantity: ${quantity}`);
              }
            });
          }
        });

        // --- Part 3: Set all state at the end ---
        console.log('Final data summary:');
        console.log('- Total packages:', tempTotalPackages);
        console.log('- Package counts:', tempPackageCounts);
        console.log('- Package prep list:', tempPackagePrepList);
        console.log('- Food summary:', tempFoodSummary);
        console.log('- Addon summary:', tempAddonSummary);

        // Update state with proper error handling
        try {
          console.log('Updating state with processed data...');
          setError(null); // Clear any existing errors
          setTotalPackages(tempTotalPackages);
          setPackageCounts(tempPackageCounts);
          setPrepByPackage(tempPackagePrepList);
          setFoodSummary(tempFoodSummary);
          setAddonSummary(tempAddonSummary);

          const availableMeals = MEAL_ORDER.filter(meal => tempFoodSummary[meal]);
          setActiveMealTab(availableMeals.length > 0 ? availableMeals[0] : '');
          console.log("State updates completed successfully.");
        } catch (stateError) {
          console.error("Error updating state:", stateError);
          setError("Error updating dashboard state. Please try again.");
        }

      } catch (err) {
        console.error("CRITICAL ERROR in getKitchenData:", err);
        console.error("Error details:", {
          message: err.message,
          stack: err.stack,
          name: err.name
        });
        setError("An error occurred while loading kitchen data. Please check the console for details.");
      } finally {
        setLoading(false);
        console.log("getKitchenData function completed.");
      }
    };

    getKitchenData();
  }, [selectedDate]);

  // --- NEW USE EFFECT TO FETCH PRODUCTS WHEN MODAL OPENS ---
  useEffect(() => {
    if (showRequestModal) {
      fetchProducts();
    }
  }, [showRequestModal]);

  // A helper to get the available meals in the correct order for rendering tabs
  const orderedMeals = MEAL_ORDER.filter(meal => foodSummary && Object.keys(foodSummary).length > 0 && foodSummary[meal]);

  // --- NEW FUNCTIONS FOR REQUEST FORM ---
  const handleRequestFormChange = (e) => {
    const { name, value } = e.target;
    setRequestFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // --- NEW FUNCTION TO FETCH AVAILABLE FOOD ITEMS ---
  const fetchAvailableFoodItems = async (date, mealTime) => {
    setLoadingFoodItems(true);
    try {
      const dayName = getDayNameFromDate(date);
      console.log(`Fetching food items for ${dayName} - ${mealTime}...`);
      console.log(`Looking for day: "${dayName}"`);

      // Get active subscriptions
      const subscriptionsRef = collection(db, 'user_subscriptions');
      const activeSubQuery = query(subscriptionsRef, where("status", "==", "active"));
      const activeSubSnapshots = await getDocs(activeSubQuery);

      const selectedDateObj = new Date(date);
      selectedDateObj.setUTCHours(0, 0, 0, 0);

      const allActiveSubscriptions = activeSubSnapshots.docs.filter(doc => {
        const data = doc.data();
        const startDate = data?.subscriptionStartDate?.toDate();
        if (!startDate) return false;
        startDate.setUTCHours(0, 0, 0, 0);
        return selectedDateObj >= startDate;
      });

      console.log(`Found ${allActiveSubscriptions.length} active subscriptions for ${date}`);

      if (allActiveSubscriptions.length === 0) {
        setAvailableFoodItems([]);
        return;
      }

      // Get unique package IDs
      const uniquePackageIds = [...new Set(allActiveSubscriptions.map(doc => doc.data().packageId))];

      // Fetch packages
      const packagesRef = collection(db, 'packages');
      const packagesQuery = query(packagesRef, where(documentId(), 'in', uniquePackageIds));
      const packageSnapshots = await getDocs(packagesQuery);
      const packagesMap = new Map();
      packageSnapshots.forEach(doc => packagesMap.set(doc.id, doc.data()));

      // Collect food items for the specific day and meal time
      const foodItemsMap = new Map(); // Use Map to avoid duplicates and track quantities

      allActiveSubscriptions.forEach(sub => {
        const packageId = sub.data().packageId;
        const pkg = packagesMap.get(packageId);

        console.log(`Processing package ${packageId}:`, pkg);
        console.log(`Package weeklyMenu:`, pkg?.weeklyMenu);

        if (pkg?.weeklyMenu) {
          // Use the getMenuForDay function for consistent menu access
          const todaysMenu = getMenuForDay(pkg.weeklyMenu, dayName, date) || {};
          console.log(`Today's menu for ${dayName}:`, todaysMenu);

          // Find the specific meal in today's menu
          const mealItems = todaysMenu[mealTime];
          console.log(`Meal items for ${mealTime}:`, mealItems);

          if (Array.isArray(mealItems)) {
            console.log(`Processing ${mealItems.length} meal items`);
            
            // Handle different meal structures
            let itemsToProcess = [];
            
            if (mealItems.length > 0 && mealItems[0].mealName) {
              // If mealItems is an array of objects with mealName and items
              const mealObject = mealItems.find(meal => meal.mealName === mealTime);
              if (mealObject && Array.isArray(mealObject.items)) {
                itemsToProcess = mealObject.items;
              } else {
                itemsToProcess = mealItems; // Fallback to direct array
              }
            } else {
              itemsToProcess = mealItems; // Direct array of items
            }
            
            console.log(`Items to process for ${mealTime}:`, itemsToProcess);
            
            itemsToProcess.forEach(item => {
              const foodName = item.foodItemName;
              const quantity = Number(item.quantity) || 0;

              if (foodName && quantity > 0) {
                if (foodItemsMap.has(foodName)) {
                  // Add to existing quantity
                  const existing = foodItemsMap.get(foodName);
                  existing.totalQuantity += quantity;
                  existing.subscriptionCount += 1;
                } else {
                  // Create new entry
                  foodItemsMap.set(foodName, {
                    foodItemName: foodName,
                    totalQuantity: quantity,
                    unit: item.unit || 'servings',
                    subscriptionCount: 1
                  });
                }
              }
            });
          }
        }
      });

      // Convert Map to Array
      const foodItemsArray = Array.from(foodItemsMap.values());
      console.log(`Found ${foodItemsArray.length} unique food items for ${dayName} - ${mealTime}`);
      console.log('Food items:', foodItemsArray);
      setAvailableFoodItems(foodItemsArray);

    } catch (error) {
      console.error('Error fetching available food items:', error);
      setAvailableFoodItems([]);
    } finally {
      setLoadingFoodItems(false);
    }
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    setSubmittingRequest(true);

    try {
      console.log('Starting request submission...');
      console.log('Selected ingredients:', selectedIngredients);

      // Validate that at least one ingredient has quantity
      const validIngredients = selectedIngredients.filter(ingredient =>
        ingredient.quantity && ingredient.quantity > 0
      );

      console.log('Valid ingredients:', validIngredients);

      if (validIngredients.length === 0) {
        alert('Please add at least one ingredient with quantity.');
        setSubmittingRequest(false);
        return;
      }

      const requestData = {
        mealTime: requestFormData.mealTime,
        specialInstructions: requestFormData.specialInstructions,
        priority: requestFormData.priority,
        foodItems: availableFoodItems, // Store the food items that need to be cooked
        ingredients: validIngredients.map(ingredient => ({
          ...ingredient,
          quantity: Number(ingredient.quantity)
        })),
        date: selectedDate,
        createdAt: Timestamp.now(),
        status: 'pending'
      };

      console.log('Request data to be submitted:', requestData);

      const docRef = await addDoc(collection(db, 'kitchen_requests'), requestData);
      console.log('Request submitted successfully with ID:', docRef.id);

      // Reset form and close modal
      setRequestFormData({
        mealTime: '',
        specialInstructions: '',
        priority: 'normal',
        items: []
      });
      setSelectedIngredients([]);
      setShowRequestModal(false);

      // Show success message
      alert('Request submitted successfully!');

    } catch (error) {
      console.error('Error submitting request:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      alert('Error submitting request. Please try again.');
    } finally {
      setSubmittingRequest(false);
    }
  };

  const resetRequestForm = () => {
    setRequestFormData({
      mealTime: '',
      specialInstructions: '',
      priority: 'normal',
      items: []
    });
    setSelectedIngredients([]);
  };

  // --- NEW FUNCTION TO FETCH PRODUCTS ---
  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const productsRef = collection(db, 'products');
      const productsSnapshot = await getDocs(productsRef);
      const productsList = productsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProducts(productsList);
      console.log('Fetched products:', productsList);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  // --- NEW FUNCTION TO ADD INGREDIENT TO REQUEST ---
  const addIngredientToRequest = (product) => {
    const newIngredient = {
      productId: product.id,
      productName: product.name,
      quantity: '',
      unit: product.unit || 'kg'
    };
    setSelectedIngredients(prev => [...prev, newIngredient]);
  };

  // --- NEW FUNCTION TO REMOVE INGREDIENT FROM REQUEST ---
  const removeIngredientFromRequest = (index) => {
    setSelectedIngredients(prev => prev.filter((_, i) => i !== index));
  };

  // --- NEW FUNCTION TO UPDATE INGREDIENT QUANTITY ---
  const updateIngredientQuantity = (index, quantity) => {
    setSelectedIngredients(prev =>
      prev.map((ingredient, i) =>
        i === index ? { ...ingredient, quantity } : ingredient
      )
    );
  };

  // --- NEW FUNCTION TO HANDLE MEAL TIME CHANGE ---
  const handleMealTimeChange = (mealTime) => {
    setRequestFormData(prev => ({
      ...prev,
      mealTime,
      items: [] // Reset items when meal time changes
    }));

    // Fetch food items for the selected meal time
    if (mealTime) {
      fetchAvailableFoodItems(selectedDate, mealTime);
    } else {
      setAvailableFoodItems([]);
    }
  };

  // --- NEW FUNCTION TO ADD FOOD ITEM TO REQUEST ---
  const addFoodItemToRequest = (foodItem) => {
    setRequestFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        foodItemName: foodItem.foodItemName,
        quantity: foodItem.totalQuantity.toString(),
        unit: foodItem.unit
      }]
    }));
  };

  // --- NEW FUNCTION TO REMOVE FOOD ITEM FROM REQUEST ---
  const removeFoodItemFromRequest = (index) => {
    setRequestFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // --- NEW USE EFFECT TO FETCH PRODUCTS WHEN MODAL OPENS ---
  useEffect(() => {
    if (showRequestModal) {
      fetchProducts();
    }
  }, [showRequestModal]);

  // --- NEW FUNCTION TO LOG ALL PACKAGE DATA ---
  const logAllPackageData = async () => {
    try {
      const packagesRef = collection(db, 'packages');
      const allPackagesSnapshot = await getDocs(packagesRef);
      console.log('=== ALL PACKAGE DATA ===');
      allPackagesSnapshot.forEach(doc => {
        console.log(`Package ${doc.id}:`, doc.data());
        if (doc.data().weeklyMenu) {
          const weeklyMenu = doc.data().weeklyMenu;
          console.log(`  Weekly menu type:`, Array.isArray(weeklyMenu) ? 'Array' : 'Object');
          console.log(`  Weekly menu keys:`, Object.keys(weeklyMenu));
          
          if (Array.isArray(weeklyMenu)) {
            console.log(`  Array structure with dayName objects:`);
            weeklyMenu.forEach((dayMenu, index) => {
              console.log(`    ${index}: ${dayMenu.dayName} - ${dayMenu.meals?.length || 0} meals`);
              if (dayMenu.meals) {
                dayMenu.meals.forEach((meal, mealIndex) => {
                  console.log(`      Meal ${mealIndex}: ${meal.mealName} - ${meal.items?.length || 0} items`);
                  if (meal.items) {
                    meal.items.forEach((item, itemIndex) => {
                      console.log(`        Item ${itemIndex}: ${item.foodItemName} (${item.quantity})`);
                    });
                  }
                });
              }
            });
          } else {
            Object.entries(weeklyMenu).forEach(([day, menu]) => {
              console.log(`    ${day}:`, menu);
            });
          }
        }
      });
      console.log('=== END PACKAGE DATA ===');
    } catch (error) {
      console.error('Error fetching all package data:', error);
    }
  };

  // --- NEW FUNCTION TO FETCH CHEFS ---
  const fetchChefs = async () => {
    setLoadingChefs(true);
    try {
      const chefsRef = collection(db, 'chefs');
      const snapshot = await getDocs(chefsRef);
      const chefsList = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(chef => chef.status === 'active'); // Only active chefs
      setChefs(chefsList);
      console.log('Fetched chefs:', chefsList);
    } catch (error) {
      console.error('Error fetching chefs:', error);
    } finally {
      setLoadingChefs(false);
    }
  };

  // --- NEW FUNCTION TO FETCH EXISTING ASSIGNMENTS ---
  const fetchExistingAssignments = async () => {
    try {
      const assignmentsRef = collection(db, 'chef_assignments');
      const assignmentsQuery = query(
        assignmentsRef,
        where('date', '==', selectedDate)
      );
      const snapshot = await getDocs(assignmentsQuery);
      const assignments = {};
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const key = `${data.foodItemName}_${data.mealTime}`;
        assignments[key] = {
          id: doc.id,
          chefId: data.chefId,
          chefName: data.chefName
        };
      });
      setChefAssignments(assignments);
      console.log('Existing assignments:', assignments);
    } catch (error) {
      console.error('Error fetching existing assignments:', error);
    }
  };

  // --- NEW FUNCTION TO ASSIGN CHEF TO FOOD ITEM ---
  const handleChefAssignment = async (foodItemName, mealTime, chefId) => {
    if (!chefId) {
      alert('Please select a chef.');
      return;
    }

    const chef = chefs.find(c => c.id === chefId);
    if (!chef) {
      alert('Could not find the selected chef.');
      return;
    }

    console.log(`Assigning ${foodItemName} for ${mealTime} to ${chef.name}`);

    try {
      const assignmentData = {
        foodItemName: foodItemName,
        mealTime: mealTime,
        date: selectedDate,
        chefId: chef.id,
        chefName: chef.name,
        assignedAt: new Date(),
        status: 'assigned'
      };

      // Check if assignment already exists
      const assignmentKey = `${foodItemName}_${mealTime}`;
      const existingAssignment = chefAssignments[assignmentKey];

      if (existingAssignment) {
        // Update existing assignment
        await updateDoc(doc(db, 'chef_assignments', existingAssignment.id), {
          chefId: chef.id,
          chefName: chef.name,
          updatedAt: new Date()
        });
      } else {
        // Create new assignment
        await addDoc(collection(db, 'chef_assignments'), assignmentData);
      }

      // Update local state
      setChefAssignments(prev => ({
        ...prev,
        [assignmentKey]: {
          chefId: chef.id,
          chefName: chef.name
        }
      }));

      alert(`Successfully assigned ${foodItemName} to ${chef.name}`);
    } catch (error) {
      console.error('Error assigning chef:', error);
      alert('Error assigning chef. Please try again.');
    }
  };

  // --- NEW FUNCTION TO LOG ALL SUBSCRIPTION DATA ---
  const logAllSubscriptionData = async () => {
    try {
      const subscriptionsRef = collection(db, 'user_subscriptions');
      const allSubscriptionsSnapshot = await getDocs(subscriptionsRef);
      console.log('=== ALL SUBSCRIPTION DATA ===');
      allSubscriptionsSnapshot.forEach(doc => {
        const data = doc.data();
        console.log(`Subscription ${doc.id}:`, {
          packageId: data.packageId,
          status: data.status,
          startDate: data.subscriptionStartDate,
          customerName: data.customerName,
          customerEmail: data.customerEmail
        });
      });
      console.log('=== END SUBSCRIPTION DATA ===');
    } catch (error) {
      console.error('Error fetching all subscription data:', error);
    }
  };

  // --- RETURN JSX --- (no changes needed here)
  return (
    <div className="kitchen-dashboard">
      <div className="dashboard-header">
        <h1>Kitchen Dashboard</h1>
        <div className="day-selector">{weekDays.map(day => <button key={day.date} className={`day-btn ${selectedDate === day.date ? 'active' : ''}`} onClick={() => setSelectedDate(day.date)}><span className="day-name">{day.dayName}</span><span className="day-number">{day.dayNumber}</span></button>)}</div>
        <div className="view-toggle"><button className={`toggle-btn ${activeView === 'packages' ? 'active' : ''}`} onClick={() => setActiveView('packages')}>Package Assembly</button><button className={`toggle-btn ${activeView === 'summary' ? 'active' : ''}`} onClick={() => setActiveView('summary')}>Ingredients Summary</button></div>
        <div className="request-button-container">
          <button
            className="request-btn"
            onClick={() => setShowRequestModal(true)}
          >
            <span className="request-icon">+</span>
            Add Cooking Request
          </button>
        </div>
      </div>

      {loading ? (<div className="loading-container">Calculating Prep List...</div>)
        : error ? (<div className="error-container">{error}</div>)
          : (
            <>
              <div className="stats-container">
                <div className="stat-card"><h3>{totalPackages}</h3><p>Packages Today</p></div>
                <div className="stat-card"><h3>{Object.keys(prepByPackage).length}</h3><p>Package Types</p></div>
                <div className="stat-card"><h3>{orderedMeals.length}</h3><p>Meals to Prepare</p></div>
              </div>

              {activeView === 'packages' ? (
                <div className="package-prep-grid">
                  {Object.keys(prepByPackage).length > 0 ? Object.entries(prepByPackage).map(([packageId, pkg]) => (
                    <div key={packageId} className="package-prep-card">
                      <div className="prep-card-header"><h3>{pkg.packageName}</h3><span className="prep-card-count">Make: {packageCounts[packageId] || 0}</span></div>
                      <div className="prep-card-body">
                        {pkg.meals && Array.isArray(pkg.meals) && pkg.meals.length > 0 ? (
                          pkg.meals.map((meal, mealIndex) => (
                            <div key={mealIndex} className="meal-section">
                              <h4 className="meal-title">{meal.mealName || `Meal ${mealIndex}`}</h4>
                              <ul>
                                {meal.items && Array.isArray(meal.items) && meal.items.map((item, index) => {
                                  const assignmentKey = `${item.foodItemName}_${meal.mealName}`;
                                  const currentAssignment = chefAssignments[assignmentKey];
                                  
                                  return (
                                    <li key={index} className="food-item-with-assignment">
                                      <div className="food-item-info">
                                        <span className="food-name">{item.foodItemName}</span>
                                        <span className="food-quantity">{item.quantity} {item.unit || 'unit(s)'}</span>
                                      </div>
                                      <div className="chef-assignment-section">
                                        {currentAssignment ? (
                                          <div className="assigned-chef">
                                            <span className="chef-label">Assigned to:</span>
                                            <span className="chef-name">{currentAssignment.chefName}</span>
                                          </div>
                                        ) : (
                                          <div className="no-assignment">
                                            <span className="chef-label">No chef assigned</span>
                                          </div>
                                        )}
                                        <div className="chef-assignment-controls">
                                          <select
                                            className="chef-select"
                                            onChange={(e) => handleChefAssignment(item.foodItemName, meal.mealName, e.target.value)}
                                            value={currentAssignment?.chefId || ''}
                                          >
                                            <option value="">Select Chef</option>
                                            {chefs.map(chef => (
                                              <option key={chef.id} value={chef.id}>
                                                {chef.name} ({chef.specialization || 'General'})
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          ))
                        ) : (
                          <p>No menu set for today.</p>
                        )}
                      </div>
                    </div>
                  )) : <div className="no-data-cell">No packages have a menu scheduled for this day.</div>}
                </div>
              ) : (
                <div className="summary-view-container">
                  <h2>Total Ingredients Summary for {getDayNameFromDate(selectedDate)}</h2>

                  {orderedMeals.length > 0 ? (
                    <div className="summary-card">
                      <h3 className="summary-card-header">Package Ingredients</h3>
                      <div className="summary-tabs">
                        {orderedMeals.map(mealName => (
                          <button key={mealName} className={`summary-tab-btn ${activeMealTab === mealName ? 'active' : ''}`} onClick={() => setActiveMealTab(mealName)}>{mealName}</button>
                        ))}
                      </div>
                      <div className="summary-tab-content">
                        {activeMealTab && foodSummary[activeMealTab] && (
                          <div className="table-container">
                            <table className="prep-table">
                              <thead><tr><th>Food Item</th><th>Total Quantity</th><th>Unit</th></tr></thead>
                              <tbody>
                                {Object.entries(foodSummary[activeMealTab]).map(([name, details]) => (
                                  <tr key={name}>
                                    <td data-label="Food Item">{name}</td>
                                    <td data-label="Total Quantity">{details.totalQuantity.toFixed(2)}</td>
                                    <td data-label="Unit">{details.unit}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="no-data-cell">No package ingredients for this day's menu.</div>
                  )}

                  {Object.keys(addonSummary).length > 0 && (
                    <div className="summary-card addon-summary-card">
                      <h3 className="summary-card-header">Add-On Ingredients</h3>
                      <div className="table-container">
                        <table className="prep-table">
                          <thead><tr><th>Food Item</th><th>Total Quantity</th></tr></thead>
                          <tbody>
                            {Object.entries(addonSummary).map(([name, quantity]) => (
                              <tr key={name}>
                                <td data-label="Food Item">{name}</td>
                                <td data-label="Total Quantity">{quantity}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {Object.keys(chefAssignments).length > 0 && (
                    <div className="summary-card chef-assignments-card">
                      <h3 className="summary-card-header">Chef Assignments</h3>
                      <div className="table-container">
                        <table className="prep-table">
                          <thead><tr><th>Food Item</th><th>Meal Time</th><th>Assigned Chef</th><th>Specialization</th></tr></thead>
                          <tbody>
                            {Object.entries(chefAssignments).map(([key, assignment]) => {
                              const [foodItemName, mealTime] = key.split('_');
                              const chef = chefs.find(c => c.id === assignment.chefId);
                              return (
                                <tr key={key}>
                                  <td data-label="Food Item">{foodItemName}</td>
                                  <td data-label="Meal Time">{mealTime}</td>
                                  <td data-label="Assigned Chef">{assignment.chefName}</td>
                                  <td data-label="Specialization">{chef?.specialization || 'General'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

      {/* Request Modal */}
      {showRequestModal && (
        <div className="modal-overlay" onClick={() => setShowRequestModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Cooking Request</h2>
              <button
                className="modal-close-btn"
                onClick={() => {
                  setShowRequestModal(false);
                  resetRequestForm();
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmitRequest} className="request-form">
                <div className="form-group">
                  <label htmlFor="mealTime">Meal Time *</label>
                  <select
                    id="mealTime"
                    name="mealTime"
                    value={requestFormData.mealTime}
                    onChange={(e) => handleMealTimeChange(e.target.value)}
                    required
                    className="form-control"
                  >
                    <option value="">Select Meal Time</option>
                    <option value="Breakfast">Breakfast</option>
                    <option value="Lunch">Lunch</option>
                    <option value="Dinner">Dinner</option>
                    <option value="Snack">Snack</option>
                  </select>
                </div>

                {requestFormData.mealTime && (
                  <div className="form-group">
                    <label>Food Items to Cook for {getDayNameFromDate(selectedDate)} - {requestFormData.mealTime}</label>
                    {loadingFoodItems ? (
                      <div className="loading-food-items">Loading food items...</div>
                    ) : availableFoodItems.length > 0 ? (
                      <div className="food-items-info">
                        {availableFoodItems.map((foodItem, index) => (
                          <div key={index} className="food-item-info-card">
                            <div className="food-item-info">
                              <h4>{foodItem.foodItemName}</h4>
                              <p>Total Quantity: {foodItem.totalQuantity} {foodItem.unit}</p>
                              <p>Subscriptions: {foodItem.subscriptionCount}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="no-food-items">
                        <p>No food items scheduled for {getDayNameFromDate(selectedDate)} - {requestFormData.mealTime}</p>
                        <p>This could mean:</p>
                        <ul>
                          <li>No active subscriptions for this date</li>
                          <li>No menu set for this day and meal time</li>
                          <li>All subscriptions are inactive</li>
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {availableFoodItems.length > 0 && (
                  <div className="form-group">
                    <label>Select Ingredients to Prepare</label>
                    {loadingProducts ? (
                      <div className="loading-products">Loading products...</div>
                    ) : (
                      <div className="products-selection">
                        <div className="available-products">
                          <h4>Available Products</h4>
                          <div className="products-grid">
                            {products.map((product) => (
                              <div key={product.id} className="product-card">
                                <div className="product-info">
                                  <h5>{product.name}</h5>
                                  <p>Unit: {product.unit || 'kg'}</p>
                                </div>
                                <button
                                  type="button"
                                  className="add-product-btn"
                                  onClick={() => addIngredientToRequest(product)}
                                >
                                  Add
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>

                        {selectedIngredients.length > 0 && (
                          <div className="selected-ingredients">
                            <h4>Selected Ingredients</h4>
                            <div className="ingredients-list">
                              {selectedIngredients.map((ingredient, index) => (
                                <div key={index} className="ingredient-row">
                                  <div className="ingredient-info">
                                    <span className="ingredient-name">{ingredient.productName}</span>
                                    <span className="ingredient-unit">({ingredient.unit})</span>
                                  </div>
                                  <div className="ingredient-quantity">
                                    <input
                                      type="number"
                                      value={ingredient.quantity}
                                      onChange={(e) => updateIngredientQuantity(index, e.target.value)}
                                      placeholder="Quantity"
                                      min="0"
                                      step="0.01"
                                      className="quantity-input"
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    className="remove-ingredient-btn"
                                    onClick={() => removeIngredientFromRequest(index)}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="specialInstructions">Special Instructions</label>
                  <textarea
                    id="specialInstructions"
                    name="specialInstructions"
                    value={requestFormData.specialInstructions}
                    onChange={handleRequestFormChange}
                    className="form-control"
                    rows="3"
                    placeholder="Any special cooking instructions, dietary requirements, or notes..."
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="priority">Priority</label>
                  <select
                    id="priority"
                    name="priority"
                    value={requestFormData.priority}
                    onChange={handleRequestFormChange}
                    className="form-control"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setShowRequestModal(false);
                      resetRequestForm();
                    }}
                    disabled={submittingRequest}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={submittingRequest || selectedIngredients.length === 0}
                  >
                    {submittingRequest ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



export default KitchenDashboard;